import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, DollarSign, Package, Users, Play, Download,
  Settings, ChevronRight, AlertTriangle
} from 'lucide-react';
import { getDatasets, createForecast, getForecasts, exportForecastReport } from '../services/api';
import { format } from 'date-fns';

function Forecasting() {
  const [datasets, setDatasets] = useState([]);
  const [forecasts, setForecasts] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState('');
  const [predictionType, setPredictionType] = useState('revenue');
  const [forecastPeriods, setForecastPeriods] = useState(6);
  const [dateColumn, setDateColumn] = useState('');
  const [valueColumn, setValueColumn] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [currentForecast, setCurrentForecast] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Demo data for immediate visualization
  const demoForecastData = {
    revenue: [
      { date: '2024-01', actual: 45000, predicted: 44500, lower: 42000, upper: 47000 },
      { date: '2024-02', actual: 52000, predicted: 51000, lower: 48500, upper: 53500 },
      { date: '2024-03', actual: 48000, predicted: 49000, lower: 46500, upper: 51500 },
      { date: '2024-04', actual: 61000, predicted: 58000, lower: 55500, upper: 60500 },
      { date: '2024-05', actual: 55000, predicted: 56500, lower: 54000, upper: 59000 },
      { date: '2024-06', actual: 67000, predicted: 65000, lower: 62500, upper: 67500 },
      { date: '2024-07', predicted: 72000, lower: 68000, upper: 76000 },
      { date: '2024-08', predicted: 78000, lower: 73000, upper: 83000 },
      { date: '2024-09', predicted: 82000, lower: 76000, upper: 88000 },
    ],
    demand: [
      { date: '2024-01', actual: 1200, predicted: 1180 },
      { date: '2024-02', actual: 1350, predicted: 1320 },
      { date: '2024-03', actual: 1100, predicted: 1150 },
      { date: '2024-04', actual: 1450, predicted: 1400 },
      { date: '2024-05', actual: 1380, predicted: 1420 },
      { date: '2024-06', actual: 1550, predicted: 1500 },
      { date: '2024-07', predicted: 1620 },
      { date: '2024-08', predicted: 1700 },
      { date: '2024-09', predicted: 1780 },
    ],
    churn: [
      { name: 'Low Risk (0-30)', customers: 650, percentage: 65 },
      { name: 'Medium Risk (30-50)', customers: 200, percentage: 20 },
      { name: 'High Risk (50-70)', customers: 120, percentage: 12 },
      { name: 'Critical (70+)', customers: 30, percentage: 3 },
    ]
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [datasetsRes, forecastsRes] = await Promise.all([
        getDatasets().catch(() => ({ datasets: [] })),
        getForecasts().catch(() => ({ forecasts: [] }))
      ]);
      setDatasets(datasetsRes.datasets || []);
      setForecasts(forecastsRes.forecasts || []);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDatasetChange = (datasetId) => {
    setSelectedDataset(datasetId);
    if (datasetId) {
      const dataset = datasets.find(d => d._id === datasetId);
      if (dataset) {
        // Auto-select columns from metadata or first available
        const dateCol = dataset.metadata?.dateColumn ||
          dataset.columns?.find(c => c.type === 'date')?.name || '';
        const valueCol = dataset.metadata?.valueColumns?.[0] ||
          dataset.columns?.find(c => c.type === 'numeric')?.name || '';
        setDateColumn(dateCol);
        setValueColumn(valueCol);
      }
    } else {
      setDateColumn('');
      setValueColumn('');
    }
  };

  const getSelectedDataset = () => datasets.find(d => d._id === selectedDataset);

  const handleGenerateForecast = async () => {
    if (!selectedDataset) {
      setError('Please select a dataset first');
      return;
    }

    if (predictionType !== 'churn' && (!dateColumn || !valueColumn)) {
      setError('Please select date and value columns');
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      setSuccess(null);

      const parameters = {
        forecastPeriods,
        dateColumn,
        valueColumn,
        aggregation: 'month'
      };

      const response = await createForecast(selectedDataset, predictionType, parameters);
      setCurrentForecast(response.forecast);
      setSuccess(`Forecast generated successfully! Accuracy: ${response.forecast.metrics?.accuracy?.toFixed(1)}%`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate forecast');
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async () => {
    if (!currentForecast) return;

    try {
      const report = await exportForecastReport(currentForecast._id);
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `forecast_report_${format(new Date(), 'yyyy-MM-dd')}.json`;
      a.click();
    } catch (err) {
      setError('Failed to export report');
    }
  };

  const predictionTypes = [
    { id: 'revenue', label: 'Revenue Forecast', icon: DollarSign, color: '#3b82f6', description: 'Predict future revenue using time series analysis' },
    { id: 'demand', label: 'Demand Prediction', icon: Package, color: '#10b981', description: 'Forecast product or service demand' },
    { id: 'churn', label: 'Churn Risk', icon: Users, color: '#f59e0b', description: 'Identify customers at risk of churning' },
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const renderForecastChart = () => {
    // Use actual forecast data if available, otherwise show demo data
    let data;
    if (currentForecast && currentForecast.predictionType === predictionType) {
      // Combine historical and prediction data for chart
      const historical = (currentForecast.historicalData || []).map(d => ({
        date: new Date(d.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
        actual: d.actual,
        predicted: d.predicted
      }));
      const predictions = (currentForecast.predictions || []).map(d => ({
        date: new Date(d.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
        predicted: Math.round(d.value),
        lower: Math.round(d.lowerBound),
        upper: Math.round(d.upperBound)
      }));
      data = [...historical, ...predictions];
    } else {
      data = demoForecastData[predictionType];
    }

    if (predictionType === 'churn') {
      return (
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
            <YAxis stroke="#6b7280" fontSize={12} />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
            />
            <Bar dataKey="customers" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Customers" />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={350}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="colorConfidence" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
          <YAxis
            stroke="#6b7280"
            fontSize={12}
            tickFormatter={(v) => predictionType === 'revenue' ? `$${v/1000}k` : v}
          />
          <Tooltip
            formatter={(value, name) => [
              predictionType === 'revenue' ? formatCurrency(value) : value,
              name
            ]}
            contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
          />
          <Legend />
          {predictionType === 'revenue' && (
            <Area
              type="monotone"
              dataKey="upper"
              stroke="transparent"
              fill="url(#colorConfidence)"
              name="Confidence Interval"
            />
          )}
          <Line
            type="monotone"
            dataKey="actual"
            stroke="#10b981"
            strokeWidth={2}
            dot={{ r: 4 }}
            name="Actual"
          />
          <Line
            type="monotone"
            dataKey="predicted"
            stroke="#3b82f6"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ r: 4 }}
            name="Predicted"
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="forecasting">
      <div className="page-header">
        <h2>Forecasting & Predictions</h2>
        <p>Generate AI-powered forecasts from your business data</p>
      </div>

      <div className="grid-3" style={{ marginBottom: '1.5rem' }}>
        {predictionTypes.map((type) => (
          <div
            key={type.id}
            className="card"
            onClick={() => setPredictionType(type.id)}
            style={{
              cursor: 'pointer',
              border: predictionType === type.id ? `2px solid ${type.color}` : '2px solid transparent',
              transition: 'all 0.2s'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 8,
                background: `${type.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <type.icon size={24} color={type.color} />
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{type.label}</h4>
                <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>{type.description}</p>
              </div>
              {predictionType === type.id && (
                <div style={{ color: type.color }}>
                  <ChevronRight size={20} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="grid-2" style={{ gap: '1.5rem' }}>
        {/* Configuration Panel */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">
              <Settings size={18} style={{ marginRight: '0.5rem' }} />
              Configuration
            </h3>
          </div>

          {error && (
            <div style={{
              background: '#fee2e2',
              color: '#991b1b',
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <AlertTriangle size={18} />
              {error}
            </div>
          )}

          {success && (
            <div style={{
              background: '#d1fae5',
              color: '#065f46',
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <TrendingUp size={18} />
              {success}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Select Dataset</label>
            <select
              className="form-select"
              value={selectedDataset}
              onChange={(e) => handleDatasetChange(e.target.value)}
            >
              <option value="">Choose a dataset...</option>
              {datasets.map((ds) => (
                <option key={ds._id} value={ds._id}>
                  {ds.name} ({ds.rowCount} rows)
                </option>
              ))}
            </select>
          </div>

          {selectedDataset && predictionType !== 'churn' && (
            <>
              <div className="form-group">
                <label className="form-label">Date Column</label>
                <select
                  className="form-select"
                  value={dateColumn}
                  onChange={(e) => setDateColumn(e.target.value)}
                >
                  <option value="">Select date column...</option>
                  {getSelectedDataset()?.columns?.map((col) => (
                    <option key={col.name} value={col.name}>
                      {col.name} ({col.type})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Value Column</label>
                <select
                  className="form-select"
                  value={valueColumn}
                  onChange={(e) => setValueColumn(e.target.value)}
                >
                  <option value="">Select value column...</option>
                  {getSelectedDataset()?.columns?.filter(c => c.type === 'numeric').map((col) => (
                    <option key={col.name} value={col.name}>
                      {col.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className="form-group">
            <label className="form-label">Forecast Periods</label>
            <select
              className="form-select"
              value={forecastPeriods}
              onChange={(e) => setForecastPeriods(parseInt(e.target.value))}
            >
              <option value={3}>3 Periods</option>
              <option value={6}>6 Periods</option>
              <option value={12}>12 Periods</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
            <button
              className="btn btn-primary"
              onClick={handleGenerateForecast}
              disabled={generating}
              style={{ flex: 1 }}
            >
              {generating ? (
                <div className="spinner" style={{ width: 20, height: 20 }}></div>
              ) : (
                <>
                  <Play size={18} />
                  Generate Forecast
                </>
              )}
            </button>
            <button
              className="btn btn-outline"
              onClick={handleExport}
              disabled={!currentForecast}
            >
              <Download size={18} />
            </button>
          </div>

          {/* Model Info */}
          <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Model Information</h4>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              <p><strong>Type:</strong> {predictionType === 'revenue' ? 'Time Series (Holt-Winters)' : predictionType === 'demand' ? 'Gradient Boosting' : 'Random Forest'}</p>
              <p><strong>Confidence Level:</strong> 95%</p>
            </div>
          </div>
        </div>

        {/* Metrics Panel */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Forecast Metrics</h3>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem' }}>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Accuracy</p>
              <p style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>
                {currentForecast?.metrics?.accuracy?.toFixed(1) || '94.2'}%
              </p>
            </div>
            <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem' }}>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>MAPE</p>
              <p style={{ fontSize: '1.5rem', fontWeight: 700 }}>
                {currentForecast?.metrics?.mape?.toFixed(1) || '5.8'}%
              </p>
            </div>
            <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem' }}>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Trend</p>
              <p style={{ fontSize: '1.5rem', fontWeight: 700, color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <TrendingUp size={20} /> {currentForecast?.summary?.trendPercentage?.toFixed(1) || '+12.5'}%
              </p>
            </div>
            <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem' }}>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>R² Score</p>
              <p style={{ fontSize: '1.5rem', fontWeight: 700 }}>
                {currentForecast?.metrics?.r2Score?.toFixed(2) || '0.89'}
              </p>
            </div>
          </div>

          {/* Insights */}
          <div style={{ marginTop: '1rem' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Key Insights</h4>
            <ul style={{ fontSize: '0.875rem', color: '#4b5563', paddingLeft: '1.25rem' }}>
              {currentForecast?.insights?.length > 0 ? (
                currentForecast.insights.map((insight, idx) => (
                  <li key={idx} style={{ marginBottom: '0.5rem' }}>{insight.message}</li>
                ))
              ) : (
                <>
                  <li style={{ marginBottom: '0.5rem' }}>Strong upward trend detected with projected growth</li>
                  <li style={{ marginBottom: '0.5rem' }}>Seasonal pattern identified with Q4 peaks</li>
                  <li>High confidence in short-term predictions (1-3 periods)</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Forecast Chart */}
      <div className="chart-container" style={{ marginTop: '1.5rem' }}>
        <div className="chart-header">
          <h3 className="chart-title">
            {predictionType === 'revenue' ? 'Revenue Forecast' :
             predictionType === 'demand' ? 'Demand Prediction' : 'Churn Risk Distribution'}
          </h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <span className="badge badge-success">Actual</span>
            <span className="badge badge-info">Predicted</span>
          </div>
        </div>
        {renderForecastChart()}
      </div>

      {/* Recent Forecasts */}
      {forecasts.length > 0 && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <div className="card-header">
            <h3 className="card-title">Recent Forecasts</h3>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Dataset</th>
                  <th>Type</th>
                  <th>Model</th>
                  <th>Accuracy</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {forecasts.slice(0, 5).map((forecast) => (
                  <tr key={forecast._id}>
                    <td>{forecast.dataset?.name || 'Unknown'}</td>
                    <td style={{ textTransform: 'capitalize' }}>{forecast.predictionType}</td>
                    <td>{forecast.modelUsed?.replace('_', ' ')}</td>
                    <td>{forecast.metrics?.accuracy?.toFixed(1)}%</td>
                    <td>
                      <span className={`badge badge-${forecast.status === 'completed' ? 'success' : 'warning'}`}>
                        {forecast.status}
                      </span>
                    </td>
                    <td>{format(new Date(forecast.createdAt), 'MMM d, yyyy')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Forecasting;
